import React from 'react';
import { SKELETON_BLOCK } from '@/shared/ui/components/basic/data-state/skeletons/skeleton-primitives';

/**
 * Skeleton placeholder matching the PropertyDetailView layout:
 * full-width header card (image + title + tabs) followed by a
 * two-column grid (tab content + sidebar metric cards).
 */
export const PropertyDetailSkeleton: React.FC = () => (
  <div
    data-testid="property-detail-skeleton"
    className="flex-1 min-h-0 overflow-y-auto bg-[var(--surface-primary)] p-[var(--space-5)]"
  >
    {/* Breadcrumb line */}
    <div className={`${SKELETON_BLOCK} h-4 w-48 mb-[var(--space-4)]`} />

    {/* Unified header card */}
    <div className="rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-[var(--shadow-md)] overflow-hidden mb-[var(--space-4)]">
      {/* Image / gallery placeholder */}
      <div className={`${SKELETON_BLOCK} w-full h-56 rounded-none`} />

      {/* Title + action buttons row */}
      <div className="flex items-center justify-between gap-(--space-4) px-[var(--space-5)] py-[var(--space-4)]">
        <div className="flex flex-col gap-(--space-2) flex-1">
          <div className={`${SKELETON_BLOCK} h-6 w-2/3`} />
          <div className={`${SKELETON_BLOCK} h-4 w-1/3`} />
        </div>
        <div className="flex gap-(--space-2)">
          <div className={`${SKELETON_BLOCK} h-9 w-20`} />
          <div className={`${SKELETON_BLOCK} h-9 w-20`} />
        </div>
      </div>

      {/* Price strip */}
      <div className="px-[var(--space-5)] pb-[var(--space-4)]">
        <div className={`${SKELETON_BLOCK} h-10 w-full`} />
      </div>

      {/* Tab bar */}
      <div className="flex gap-(--space-1) px-[var(--space-5)] pb-[var(--space-3)]">
        {[100, 140, 80, 100, 110].map((w, i) => (
          <div key={i} className={`${SKELETON_BLOCK} h-8`} style={{ width: w }} />
        ))}
      </div>
    </div>

    {/* Content grid */}
    <div className="grid grid-cols-1 md:grid-cols-[1fr_340px] gap-[var(--space-4)]">
      {/* Main tab content */}
      <div className="flex flex-col gap-[var(--space-4)]">
        <div className={`${SKELETON_BLOCK} h-5 w-32`} />
        <div className={`${SKELETON_BLOCK} h-4 w-full`} />
        <div className={`${SKELETON_BLOCK} h-4 w-5/6`} />
        <div className={`${SKELETON_BLOCK} h-4 w-3/4`} />
        <div className="grid grid-cols-2 gap-[var(--space-3)] mt-[var(--space-2)]">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-(--space-1)">
              <div className={`${SKELETON_BLOCK} h-3 w-20`} />
              <div className={`${SKELETON_BLOCK} h-5 w-28`} />
            </div>
          ))}
        </div>
      </div>

      {/* Sidebar */}
      <div className="flex flex-col gap-[var(--space-4)]">
        {[3, 2, 3].map((rows, cardIndex) => (
          <div
            key={cardIndex}
            className="rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-4)] flex flex-col gap-(--space-3)"
          >
            <div className={`${SKELETON_BLOCK} h-4 w-24`} />
            {Array.from({ length: rows }).map((_, i) => (
              <div key={i} className="flex justify-between gap-(--space-2)">
                <div className={`${SKELETON_BLOCK} h-3 w-24`} />
                <div className={`${SKELETON_BLOCK} h-3 w-16`} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);
